import React from "react";
import { useLocation } from "react-router-dom";
/**
 * Per-route мета — императивно через DOM (без react-helmet-async).
 *
 * Почему не helmet: в этой сборке react-helmet-async НЕ применял теги в
 * production-бандле (первый клиентский flush не срабатывал; в dev «спасал»
 * двойной маунт StrictMode, в проде его нет → `<head>` оставался с дефолтами
 * из index.html). Императивный upsert детерминирован и не зависит от таймингов
 * библиотеки. См. docs/SEO.md.
 *
 * ВАЖНО: это клиентский рендер `<head>` — его подхватывает Google (исполняет
 * JS), но НЕ Яндекс и НЕ боты соцсетей. Для страниц из sitemap.xml эти же
 * теги попадают в HTML через пререндер (снимок отдаётся всем, см.
 * docs/SEO.md); на остальных роутах без JS видны только статические мета из
 * `public/index.html`. Поверх снимка upsert обновляет существующие теги на
 * месте — дублей title/description/canonical не появляется.
 *
 * canonical/og:url всегда указывают на ПРОД-домен (даже со staging), чтобы
 * тестовый хост не самоканоникализировался и не плодил дубли в индексе.
 */
const SITE_NAME = "Виртуоз";
export const PROD_ORIGIN = "https://virtuoz.pro";
const DEFAULT_TITLE = "Виртуоз — создать персональную песню в подарок онлайн";
/** Брендовая карточка 1200×630 для соцсетей, см. scripts/og-image.js. */
const DEFAULT_IMAGE = `${PROD_ORIGIN}/og-image.jpg`;
const DEFAULT_IMAGE_ALT = "Виртуоз — персональная песня в подарок за 5 минут";
/**
 * Дефолтные мета сайта — те же, что стоят в шаблоне `public/index.html`
 * (совпадение проверяет prerenderInfra.test.ts). Нужны в рантайме, потому что
 * документ мог прийти не из шаблона, а из снимка пререндера со СВОИМИ мета:
 * при уходе на страницу без `<Seo>` head возвращается к ним (SeoRouteDefaults).
 */
export const SEO_DEFAULTS = {
    title: DEFAULT_TITLE,
    description: "Виртуоз — персональная песня в подарок за 5 минут. Расскажите про близкого — ИИ напишет текст, споёт и сведёт готовый трек. Создайте песню онлайн.",
    canonical: `${PROD_ORIGIN}/`,
    ogTitle: "Виртуоз — создать персональную песню в подарок",
    ogDescription: "ИИ напишет текст по вашему рассказу, споёт и сведёт трек за 5 минут. Создайте песню в подарок близкому онлайн.",
    twitterDescription: "ИИ напишет текст, споёт и сведёт трек за 5 минут.",
};
/** Только эти хосты считаем прод-индексируемыми. */
export const PROD_HOSTS = ["virtuoz.pro", "www.virtuoz.pro"];
/** true только на прод-домене; на staging/localhost — false → noindex. */
export function isProdHost() {
    if (typeof window === "undefined")
        return true;
    // Пререндер (scripts/prerender.js) гоняет прод-сборку на 127.0.0.1, но снимок
    // отдаётся ботам с прод-домена. Без этой ветки в HTML для Яндекса уехал бы
    // noindex — ровно обратное тому, зачем пререндер делается.
    if (window.__PRERENDER__)
        return true;
    return PROD_HOSTS.includes(window.location.hostname);
}
/** Найти/создать <meta> по name|property и выставить content. */
export function upsertMeta(attr, key, content) {
    if (typeof document === "undefined")
        return;
    let el = document.head.querySelector(`meta[${attr}="${key}"]`);
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
    }
    el.setAttribute("content", content);
}
/** Снять <meta> — нужно для размеров og:image, когда картинка не дефолтная. */
function removeMeta(attr, key) {
    if (typeof document === "undefined")
        return;
    const el = document.head.querySelector(`meta[${attr}="${key}"]`);
    if (el && el.parentNode)
        el.parentNode.removeChild(el);
}
function upsertLink(rel, href) {
    if (typeof document === "undefined")
        return;
    let el = document.head.querySelector(`link[rel="${rel}"]`);
    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
    }
    el.setAttribute("href", href);
}
export function robotsContent(noindex) {
    if (!isProdHost())
        return "noindex, nofollow";
    return noindex ? "noindex, follow" : "index, follow";
}
/** JSON-LD-теги, созданные в этом рантайме (а не пришедшие в HTML снимка). */
const liveJsonLd = new WeakSet();
/** Сколько `<Seo>` сейчас смонтировано — есть ли у head «хозяин». */
let mountedSeoCount = 0;
/** Убрать JSON-LD, пришедший в HTML снимка (свои живые теги не трогаем). */
function removeSnapshotJsonLd() {
    document.head
        .querySelectorAll("script[data-seo-jsonld]")
        .forEach((el) => {
        if (!liveJsonLd.has(el))
            el.remove();
    });
}
/** Вернуть head к дефолтам шаблона index.html. */
export function resetSeoToDefaults() {
    if (typeof document === "undefined")
        return;
    document.title = SEO_DEFAULTS.title;
    upsertMeta("name", "description", SEO_DEFAULTS.description);
    upsertMeta("name", "robots", robotsContent(false));
    upsertLink("canonical", SEO_DEFAULTS.canonical);
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:title", SEO_DEFAULTS.ogTitle);
    upsertMeta("property", "og:description", SEO_DEFAULTS.ogDescription);
    upsertMeta("property", "og:url", SEO_DEFAULTS.canonical);
    upsertMeta("property", "og:image", DEFAULT_IMAGE);
    upsertMeta("property", "og:image:type", "image/jpeg");
    upsertMeta("property", "og:image:width", "1200");
    upsertMeta("property", "og:image:height", "630");
    upsertMeta("property", "og:image:alt", DEFAULT_IMAGE_ALT);
    upsertMeta("name", "twitter:title", SEO_DEFAULTS.ogTitle);
    upsertMeta("name", "twitter:description", SEO_DEFAULTS.twitterDescription);
    upsertMeta("name", "twitter:image", DEFAULT_IMAGE);
    upsertMeta("name", "twitter:image:alt", DEFAULT_IMAGE_ALT);
    removeSnapshotJsonLd();
}
/**
 * Страница без своего `<Seo>` не должна донашивать мета предыдущей: после
 * `/` → /generate-song во вкладке оставались title/description/canonical
 * лендинга (а с пререндером они приходят уже в HTML). На каждой смене пути
 * возвращаем head к дефолтам; страница со своим `<Seo>` тут же выставит свои.
 *
 * Порядок важен, поэтому компонент рендерится ПЕРЕД `<App />` (index.tsx):
 * React сначала выполняет все cleanup-ы ушедшей страницы, затем эффекты в
 * порядке дерева — наш первым, потом `<Seo>` новой страницы, и только потом
 * хит Метрики в App (он читает уже правильный title). `<Seo>`, переживший
 * навигацию (тот же экземпляр на новом пути), остаётся хозяином head — его не
 * трогаем.
 */
export const SeoRouteDefaults = () => {
    const { pathname } = useLocation();
    const previous = React.useRef(pathname);
    React.useEffect(() => {
        if (previous.current === pathname)
            return;
        previous.current = pathname;
        if (mountedSeoCount === 0)
            resetSeoToDefaults();
    }, [pathname]);
    return null;
};
function toAbsolute(pathOrUrl) {
    if (/^https?:\/\//i.test(pathOrUrl))
        return pathOrUrl;
    return `${PROD_ORIGIN}${pathOrUrl.startsWith("/") ? "" : "/"}${pathOrUrl}`;
}
export const Seo = ({ title, description, path, image, noindex = false, type = "website", jsonLd, }) => {
    const jsonLdStr = jsonLd ? JSON.stringify(jsonLd) : "";
    React.useEffect(() => {
        mountedSeoCount += 1;
        return () => {
            mountedSeoCount -= 1;
        };
    }, []);
    React.useEffect(() => {
        if (typeof document === "undefined")
            return;
        const pathname = path ?? (typeof window !== "undefined" ? window.location.pathname : "/");
        const canonical = `${PROD_ORIGIN}${pathname}`;
        const fullTitle = title || DEFAULT_TITLE;
        const img = image ? toAbsolute(image) : DEFAULT_IMAGE;
        document.title = fullTitle;
        upsertMeta("name", "robots", robotsContent(noindex));
        upsertLink("canonical", canonical);
        upsertMeta("property", "og:site_name", SITE_NAME);
        upsertMeta("property", "og:locale", "ru_RU");
        upsertMeta("property", "og:type", type);
        upsertMeta("property", "og:title", fullTitle);
        upsertMeta("property", "og:url", canonical);
        upsertMeta("property", "og:image", img);
        // Размеры отдаём только для брендовой карточки: у пользовательских
        // картинок (аватар, обложка подарка) они другие, а неверные width/height
        // ломают выбор большого превью в Telegram/VK сильнее, чем их отсутствие.
        if (img === DEFAULT_IMAGE) {
            upsertMeta("property", "og:image:type", "image/jpeg");
            upsertMeta("property", "og:image:width", "1200");
            upsertMeta("property", "og:image:height", "630");
            upsertMeta("property", "og:image:alt", DEFAULT_IMAGE_ALT);
            upsertMeta("name", "twitter:image:alt", DEFAULT_IMAGE_ALT);
        }
        else {
            removeMeta("property", "og:image:type");
            removeMeta("property", "og:image:width");
            removeMeta("property", "og:image:height");
            removeMeta("property", "og:image:alt");
            removeMeta("name", "twitter:image:alt");
        }
        upsertMeta("name", "twitter:card", "summary_large_image");
        upsertMeta("name", "twitter:title", fullTitle);
        upsertMeta("name", "twitter:image", img);
        if (description) {
            upsertMeta("name", "description", description);
            upsertMeta("property", "og:description", description);
            upsertMeta("name", "twitter:description", description);
        }
        // Снимок пререндера уже содержит JSON-LD своей страницы (для Яндекса без
        // JS). Когда бандл исполняется поверх снимка, старый тег остаётся — без
        // чистки на странице было бы два одинаковых графа, а после перехода на
        // страницу без JSON-LD — чужой граф. Удаляем только «чужие» теги из HTML,
        // свои живые (другие <Seo> этого рантайма) не трогаем.
        removeSnapshotJsonLd();
        let ldEl = null;
        if (jsonLdStr) {
            ldEl = document.createElement("script");
            ldEl.type = "application/ld+json";
            ldEl.setAttribute("data-seo-jsonld", "");
            ldEl.textContent = jsonLdStr;
            document.head.appendChild(ldEl);
            liveJsonLd.add(ldEl);
        }
        return () => {
            if (ldEl && ldEl.parentNode)
                ldEl.parentNode.removeChild(ldEl);
        };
    }, [title, description, path, image, noindex, type, jsonLdStr]);
    return null;
};
